import { useState, useEffect } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitButton } from "ui-components/Form";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Card } from "@mui/material";
import {
  Grid,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
  Table,
  TableCell,
  TableBody,
  TableRow,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import {
  formRender,
  initialValues,
  preferredLocationValidations,
} from "./preferred-location-schema";
import PreferredLocationInfo from "./components/PreferredLocationInfo";
import { createPreferredLocation } from "services/data.internal.service";
import Layout from "layouts/views/components/Layout";
import { useCustomerValue } from "providers/CustomerContext";
import { Success, Error } from "ui-components/Alert";
import { InboundLayout } from "ui-components/Layout";
import { Error_ } from "ui-components/Alert";
function NewPreferredLocation() {
  const { customerCode: customerValue } = useCustomerValue();
  const customerCode = customerValue;
  const [alert, setAlert] = useState(null);
  const [locationsId, setLocationsId] = useState([]);
  const [selectedCells, setSelectedCells] = useState([]);
  const [selectedSKU, setSelectedSKUs] = useState([]);
  const [routingDirection, setRoutingDirection] = useState("no-direction");
  const [routingData, setRoutingData] = useState([]);
  const [skuOptions, setSkuOptions] = useState([]);
  const [pickFaceLoc, setPickFaceLoc] = useState([]);

  const form = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(preferredLocationValidations),
  });
  const {
    register,
    control,
    formState: { errors },
    setValue,
    getValues,
    watch,
    handleSubmit,
    reset,
    trigger,
  } = form;
  const prefLocProps = {
    formRender,
    control,
    errors,
    watch,
    setValue,
    customerCode,
    locationsId,
    setLocationsId,
    selectedCells,
    setSelectedCells,
    selectedSKU,
    setSelectedSKUs,
    skuOptions,
    setSkuOptions,
    pickFaceLoc,
    setPickFaceLoc,
  };

  const [locationID, setRouteSequence] = watch(["locationID", "setRouteSequence"]);

  const createPreferredLocationMutation = useMutation(createPreferredLocation, {
    onSuccess: (data) => {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      setAlert(<Success data={data} onChange={setAlert} />)
    }
    ,
    onError: (data) =>{
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      setAlert(<Error data={data} onChange={setAlert} />)
    } 
    ,
  });

  const onSubmit = async () => {
    const isValid = await trigger();
    if (!isValid) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      return;
    }
    const values = getValues();

    if (!values.locationID || values.locationID.length === 0) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      setAlert(<Error content={"Invalid Location IDs"} onChange={setAlert} />);
      return;
    }
    if (!values.skuList || values.skuList.length === 0) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      setAlert(<Error content={"Invalid SKUs"} onChange={setAlert} />);
      return;
    }

    if (
      values.skuList.some(
        (x) =>
          (x.vendorCode === null || x.vendorCode === "") &&
          values.skuList.some(
            (y) => y.customerCode === x.customerCode && y.vendorCode !== null && y.vendorCode !== ""
          )
      )
    ) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      setAlert(
        <Error
          content={"Error inserting SKUs, existed setting for all vendors"}
          onChange={setAlert}
        />
      );
      return;
    }
    const req = {
      ...values,
      preferredLocationDetails: Boolean(setRouteSequence) ? [...routingData] : values.locationID.map(x => {
        return {locationId: x, routeSequence: 0};
      }),
    };

    createPreferredLocationMutation.mutate(
      {
        ...req,
      },
      {
        onSuccess: () => {
          reset();
          setLocationsId([]);
          setSelectedCells([]);
          setRoutingDirection("no-direction");
          setRoutingData([]);
          setSelectedSKUs([]);
          setSkuOptions([]);
          setPickFaceLoc([]);
        },
      }
    );
  };

  const routeValid = (locations) => {
    if (locations.some(x => !Boolean(x.lLevel) || !Boolean(x.bay) || !Boolean(x.slot))) {
      return false;
    }
    const seen = new Set();
    for (const item of locations) {
      const key = `${item.lLevel}-${item.bay}-${item.slot}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
    }
    return true;
  }

  const createStatus = createPreferredLocationMutation.status === "loading"; 
  return (
    <InboundLayout lg={12}>
      {alert}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Card sx={{ height: "100%" }}>
          <MDBox p={3}>
            <PreferredLocationInfo {...prefLocProps} />
            {(Boolean(setRouteSequence) && Boolean(locationID.length) && routeValid(locationsId)) && (
              <RoutingForm
                locationsId2={locationsId}
                selectedLocationsId={locationID}
                routingData={routingData}
                setRoutingData={setRoutingData}
                routingDirection={routingDirection}
                setRoutingDirection={setRoutingDirection}
              />
            )}
            {(Boolean(setRouteSequence) && Boolean(locationID.length) && !routeValid(locationsId)) && (
              <Error_ content={"Unable to draw front view"} onChange={setAlert} />
            )}
          </MDBox>
          <MDBox p={3}>
            <SubmitButton label="Submit" disabled={createStatus} url="/setup/preferred-location" />
          </MDBox>
        </Card>
      </form>
    </InboundLayout>
  );
}

function RoutingForm({
  locationsId2,
  selectedLocationsId,
  routingData,
  setRoutingData,
  routingDirection,
  setRoutingDirection,
}) {
  const locations = locationsId2;
   let maxRow = Math.max.apply(
     Math,
     locations.map((x) => x.lLevel)
   );
   let maxColumn = Math.max.apply(
     Math,
     locations.map((x) => x.bay)
   );
   let numberOfSlot = Math.max.apply(
     Math,
     locations.map((x) => x.slot)
   );
   if (numberOfSlot > 0) maxColumn = maxColumn * numberOfSlot;
   let locationsId = [];
   for (var i = 0; i < maxRow; i++) {
     let row = Array(maxColumn).fill("");
     let locationsInRow = locations.filter((x) => x.lLevel === i + 1);
     if (locationsInRow.length > 0) {
       for (var j = 0; j < locationsInRow.length; j++) {
         let index = (locationsInRow[j].bay - 1) * numberOfSlot + locationsInRow[j].slot - 1;
         row[index] = locationsInRow[j].locationID;
       }
     }
     locationsId.unshift(row);
   }

   const selectedCells = [];

  // Loop over each selected location
  selectedLocationsId.forEach((locationID) => {
    // Loop over rows
    for (let rowIndex = 0; rowIndex < locationsId.length; rowIndex++) {
      const row = locationsId[rowIndex];

      // Find the column index of the locationID in the row
      const columnIndex = row.indexOf(locationID);

      if (columnIndex !== -1) {
        selectedCells.push({ rowIndex, columnIndex });
        break; // Stop searching once found
      }
    }
  });

  const selectedRows = selectedCells.map((cell) => cell.rowIndex);
  const selectedColumns = selectedCells.map((cell) => cell.columnIndex);
  const minRowIndex = Math.min(...selectedRows);
  const maxRowIndex = Math.max(...selectedRows);
  const minColumnIndex = Math.min(...selectedColumns);
  const maxColumnIndex = Math.max(...selectedColumns);

  useEffect(() => {
    assignRouting();
  }, [selectedLocationsId, routingDirection]);

  const isCellSelected = (rowIndex, columnIndex) => {
    return selectedCells.some(
      (cell) => cell.rowIndex === rowIndex && cell.columnIndex === columnIndex
    );
  };
  const subTable = [];

  for (let rowIndex = minRowIndex; rowIndex <= maxRowIndex; rowIndex++) {
    const rowData = [];
    for (let columnIndex = minColumnIndex; columnIndex <= maxColumnIndex; columnIndex++) {
      const cellData = selectedCells.find(
        (cell) => cell.rowIndex === rowIndex && cell.columnIndex === columnIndex
      );
      if (cellData) {
        rowData.push(locationsId[cellData.rowIndex][cellData.columnIndex]);
      } else {
        rowData.push("");
      }
    }
    subTable.push(rowData);
  }

  const assignRouting = () => {
    if (selectedLocationsId.length > 0) {
      let routingValue;
      let routes = [];
      if (routingDirection == "no-direction" || routingDirection == null) {
        routingValue = Array(selectedLocationsId?.length).fill(0);
        selectedLocationsId.forEach((x, index) =>
          routes.push({ locationId: x, routeSequence: routingValue[index] })
        );
      } else {
        routingValue = Array(selectedLocationsId?.length).fill(1);
        routingValue = routingValue.map((r, index) => r * (index + 1));
        var routeIndex = 0;
        switch (routingDirection) {
          case "right-down":
          case "left-down":
            for (var row = 0; row < subTable.length; row++) {
              if (routingDirection == "right-down") {
                for (var col = 0; col < subTable[0].length; col++) {
                  if (isCellSelected(minRowIndex + row, minColumnIndex + col)) {
                    routes.push({
                      locationId: locationsId[minRowIndex + row][minColumnIndex + col],
                      routeSequence: routingValue[routeIndex++],
                    });
                  }
                }
              } else {
                for (var col = subTable[0].length; col >= 0; col--) {
                  if (isCellSelected(minRowIndex + row, minColumnIndex + col)) {
                    routes.push({
                      locationId: locationsId[minRowIndex + row][minColumnIndex + col],
                      routeSequence: routingValue[routeIndex++],
                    });
                  }
                }
              }
            }
            break;
          case "left-up":
          case "right-up":
            for (var row = subTable.length - 1; row >= 0; row--) {
              if (routingDirection == "right-up") {
                for (var col = 0; col < subTable[0].length; col++) {
                  if (isCellSelected(minRowIndex + row, minColumnIndex + col)) {
                    routes.push({
                      locationId: locationsId[minRowIndex + row][minColumnIndex + col],
                      routeSequence: routingValue[routeIndex++],
                    });
                  }
                }
              } else {
                for (var col = subTable[0].length; col >= 0; col--) {
                  if (isCellSelected(minRowIndex + row, minColumnIndex + col)) {
                    routes.push({
                      locationId: locationsId[minRowIndex + row][minColumnIndex + col],
                      routeSequence: routingValue[routeIndex++],
                    });
                  }
                }
              }
            }
            break;
          case "down-right":
          case "up-right":
            for (let col = 0; col < subTable[0].length; col++) {
              if (routingDirection == "down-right") {
                for (let row = 0; row < subTable.length; row++) {
                  if (isCellSelected(minRowIndex + row, minColumnIndex + col)) {
                    routes.push({
                      locationId: locationsId[minRowIndex + row][minColumnIndex + col],
                      routeSequence: routingValue[routeIndex++],
                    });
                  }
                }
              } else {
                for (var row = subTable.length - 1; row >= 0; row--) {
                  if (isCellSelected(minRowIndex + row, minColumnIndex + col)) {
                    routes.push({
                      locationId: locationsId[minRowIndex + row][minColumnIndex + col],
                      routeSequence: routingValue[routeIndex++],
                    });
                  }
                }
              }
            }
            break;
          case "down-left":
          case "up-left":
            for (var col = subTable[0].length; col >= 0; col--) {
              if (routingDirection == "down-left") {
                for (let row = 0; row < subTable.length; row++) {
                  if (isCellSelected(minRowIndex + row, minColumnIndex + col)) {
                    routes.push({
                      locationId: locationsId[minRowIndex + row][minColumnIndex + col],
                      routeSequence: routingValue[routeIndex++],
                    });
                  }
                }
              } else {
                for (var row = subTable.length - 1; row >= 0; row--) {
                  if (isCellSelected(minRowIndex + row, minColumnIndex + col)) {
                    routes.push({
                      locationId: locationsId[minRowIndex + row][minColumnIndex + col],
                      routeSequence: routingValue[routeIndex++],
                    });
                  }
                }
              }
            }
            break;
          default:
            break;
        }
      }
      setRoutingData(routes);
    }
  };

  const [routingSubTable, setRoutingSubTable] = useState([]);

  useEffect(() => {
    let table = subTable.map((row, rowIndex) =>
      row.map((cellData, columnIndex) => {
        if (isCellSelected(minRowIndex + rowIndex, minColumnIndex + columnIndex)) {
          let data = routingData.filter((r) => r.locationId == cellData);
          if (data[0]) return data[0];
        }
        return cellData;
      })
    );
    for (var row = 0; row < table.length; row++) {
      if (table[row].every((cell) => cell == "")) {
        table.splice(row, 1);
        row--;
      }
    }
    for (var col = 0; col < table[0].length; col++) {
      if (table.every((row) => row[col] == "")) {
        table.forEach((row) => row.splice(col, 1));
        col--;
      }
    }
    setRoutingSubTable(table);
  }, [routingData]);

  return (
    <>
      <MDBox>
        <MDBox pt={1} pb={2}>
          <MDTypography color="info" variant="h5">
            Route Sequence
          </MDTypography>
        </MDBox>
        <Table style={{ borderCollapse: "collapse", marginBottom: "10px" }}>
          <TableBody>
            {routingSubTable.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {row.map((cellData, columnIndex) => (
                  <TableCell
                    key={`${rowIndex}-${columnIndex}`}
                    style={{
                      backgroundColor: cellData != "" ? "Gainsboro" : "transparent",
                      border: "1px solid black",
                      fontWeight: "bold",
                    }}
                  >
                    {cellData != "" ? (
                      <>
                        {`${cellData.locationId}`}{" "}
                        <span style={{ color: "red", fontWeight: "bold" }}>
                          ({`${cellData.routeSequence}`})
                        </span>
                      </>
                    ) : (
                      <>{cellData}</>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </MDBox>
      <MDBox>
        <FormControl style={{ width: "100%" }}>
          <MDBox pt={1} pb={2}>
            <MDTypography color="info" variant="h5">
              Routing Direction
            </MDTypography>
          </MDBox>
          <RadioGroup
            row
            value={routingDirection}
            onChange={(e) => setRoutingDirection(e.target.value)}
          >
            <Grid container>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  style={{ marginLeft: "0px" }}
                  value="no-direction"
                  control={<Radio />}
                  label={
                    <span
                      style={{
                        fontSize: "1rem",
                        fontWeight: "300",
                        lineHeight: "1.6",
                        color: "black",
                      }}
                    >
                      No Direction
                    </span>
                  }
                />
                <FormControlLabel
                  style={{ marginLeft: "0px" }}
                  value="right-down"
                  control={<Radio />}
                  label={
                    <span
                      style={{
                        fontSize: "1rem",
                        fontWeight: "300",
                        lineHeight: "1.6",
                        color: "black",
                      }}
                    >
                      Right Down
                    </span>
                  }
                />
                <FormControlLabel
                  style={{ marginLeft: "0px" }}
                  value="left-down"
                  control={<Radio />}
                  label={
                    <span
                      style={{
                        fontSize: "1rem",
                        fontWeight: "300",
                        lineHeight: "1.6",
                        color: "black",
                      }}
                    >
                      Left Down
                    </span>
                  }
                />
                <FormControlLabel
                  style={{ marginLeft: "0px" }}
                  value="right-up"
                  control={<Radio />}
                  label={
                    <span
                      style={{
                        fontSize: "1rem",
                        fontWeight: "300",
                        lineHeight: "1.6",
                        color: "black",
                      }}
                    >
                      Right Up
                    </span>
                  }
                />
                <FormControlLabel
                  style={{ marginLeft: "0px" }}
                  value="left-up"
                  control={<Radio />}
                  label={
                    <span
                      style={{
                        fontSize: "1rem",
                        fontWeight: "300",
                        lineHeight: "1.6",
                        color: "black",
                      }}
                    >
                      Left Up
                    </span>
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  style={{ marginLeft: "0px" }}
                  value="down-right"
                  control={<Radio />}
                  label={
                    <span
                      style={{
                        fontSize: "1rem",
                        fontWeight: "300",
                        lineHeight: "1.6",
                        color: "black",
                      }}
                    >
                      Down Right
                    </span>
                  }
                />
                <FormControlLabel
                  style={{ marginLeft: "0px" }}
                  value="up-right"
                  control={<Radio />}
                  label={
                    <span
                      style={{
                        fontSize: "1rem",
                        fontWeight: "300",
                        lineHeight: "1.6",
                        color: "black",
                      }}
                    >
                      Up Right
                    </span>
                  }
                />
                <FormControlLabel
                  style={{ marginLeft: "0px" }}
                  value="down-left"
                  control={<Radio />}
                  label={
                    <span
                      style={{
                        fontSize: "1rem",
                        fontWeight: "300",
                        lineHeight: "1.6",
                        color: "black",
                      }}
                    >
                      Down Left
                    </span>
                  }
                />
                <FormControlLabel
                  style={{ marginLeft: "0px" }}
                  value="up-left"
                  control={<Radio />}
                  label={
                    <span
                      style={{
                        fontSize: "1rem",
                        fontWeight: "300",
                        lineHeight: "1.6",
                        color: "black",
                      }}
                    >
                      Up Left
                    </span>
                  }
                />
              </Grid>
            </Grid>
          </RadioGroup>
        </FormControl>
      </MDBox>
    </>
  );
}

export default NewPreferredLocation;
